// Where the racing card comes from.
//
// The same shape as `fetchSnapshot` in lib/odds.js, on purpose: resolves and
// never rejects, answers `{ …, status }` out of one small vocabulary, and puts
// every connected key in a header so the server can pick one. Anything reading
// this file should be able to read that one without learning a second idiom.
//
// NOTHING HERE DECIDES WHEN TO FETCH. That rule is in src/store.js, where the
// cache age and the tab are both visible: on demand only, cached card first,
// and past twenty minutes or a pull. A 2-credit call has to be somebody's
// decision, and it cannot be this module's.

import { apiBase } from '../api-base.js';
import { prefs, records } from '../../core/storage.js';
import { keyHeaders, setProviderCredits } from '../providers.js';

/** The provider that prices racing, and the pref its day is kept under. */
export const RACING_PROVIDER = 'puntersedge';
const CACHE = 'racingDay';

/**
 * THE CARD OUTGREW LOCALSTORAGE. A full evening card is ~7 MB of JSON (241
 * races, fifteen books a runner, each with its own race URL), and WebKit's
 * localStorage quota is ~5 MB for the whole origin: the write threw, the catch
 * below swallowed it, and after any reload (an OTA update applying, a cold
 * start) there was no card to put back. With the day's six automatic pulls
 * spent, Today then offered no racing at all until the Racing tab was opened.
 *
 * So the card is kept in IndexedDB, which has no such ceiling, under one record
 * id; localStorage keeps it too only while it is small enough to fit, which is
 * every card in the tests and a morning's card on a quiet day.
 */
export const CARD_RECORD_ID = 'cache:racing-card';
export const PREF_CARD_LIMIT = 1_500_000;

/**
 * The signed-in punter's token as a header, or nothing. The card is stored on
 * the server and handed to everyone who asks, so the server asks who is asking.
 * Imported late, so a build with the auth addon switched off still runs.
 */
async function authHeaders() {
  try {
    const mod = await import('../../../addons/auth/index.js');
    const token = await mod.getIdToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** The card already on this device, if it is the day asked for. */
export function cachedRacingCard(date) {
  try {
    const card = prefs[CACHE];
    return card && card.forDate === date ? card : null;
  } catch {
    return null;
  }
}

/** Keep a card on the device: always in IndexedDB, in prefs only if it fits. */
export function keepCard(card) {
  try {
    if (JSON.stringify(card).length <= PREF_CARD_LIMIT) prefs[CACHE] = card;
    else delete prefs[CACHE];
  } catch { /* private mode, or over quota */ }
  try {
    records.put({ id: CARD_RECORD_ID, entity: 'cache', card })?.catch?.(() => {});
  } catch { /* no IndexedDB (a test, a locked-down browser) */ }
}

/** The card IndexedDB holds for `date`, or null. Async: what a reload reads. */
export async function storedRacingCard(date) {
  try {
    const rec = await records.get(CARD_RECORD_ID);
    return rec?.card?.forDate === date && rec.card.races?.length ? rec.card : null;
  } catch {
    return null;
  }
}

/** Forget it — used when a key is removed, and by the tests. */
export function clearRacingCard() {
  try { delete prefs[CACHE]; } catch { /* private mode */ }
  try { records.delete(CARD_RECORD_ID)?.catch?.(() => {}); } catch { /* no IndexedDB */ }
}

/**
 * One request to one of the three racing routes, with the statuses the whole
 * app already speaks:
 *
 *   ready         the server answered
 *   unconfigured  no PuntersEdge key on this device
 *   signed-out    the server does not know who is asking
 *   offline       no network
 *   error         the server answered with something else
 */
async function ask(path, query = {}) {
  const headers = { Accept: 'application/json', ...keyHeaders(), ...(await authHeaders()) };
  const params = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  );
  const qs = params.toString();
  let res;
  try {
    res = await fetch(`${apiBase()}${path}${qs ? `?${qs}` : ''}`, { headers });
  } catch {
    return { body: null, status: 'offline' };
  }
  if (res.status === 401) {
    let why = null;
    try { why = (await res.clone().json())?.error ?? null; } catch { why = null; }
    return { body: null, status: why === 'sign-in-required' ? 'signed-out' : 'unconfigured' };
  }
  if (!res.ok || !/json/.test(res.headers.get('content-type') ?? '')) {
    return { body: null, status: 'error' };
  }
  try {
    const body = await res.json();
    // The meter rides on every answer and costs nothing to read, so Settings
    // shows a real balance without anybody pressing Test.
    if (body?.credits) setProviderCredits(RACING_PROVIDER, body.credits);
    return { body, status: 'ready' };
  } catch {
    return { body: null, status: 'error' };
  }
}

/**
 * A day's card: `{ card, status }`.
 *
 * `card` is `{ forDate, fetchedAt, races, credits }` — the races in PuntersEdge's
 * own shape, which `fromRaces` (board.js) turns into the rows the engine reads.
 * Only a card with races is kept on the device: an empty one is worth asking
 * for again, and it costs nothing to, because the server has it stored too.
 */
export async function fetchRacingCard(date, { categories = null } = {}) {
  const { body, status } = await ask('/api/racing', {
    date,
    categories: Array.isArray(categories) ? categories.join(',') : categories,
  });
  const card = body && Array.isArray(body.races) ? body : null;
  if (card?.races?.length) keepCard(card);
  return { card, status };
}

/** Settled races for a meeting date: `{ results, status }`. R5 reads these. */
export async function fetchRacingResults(date, { categories = null } = {}) {
  const { body, status } = await ask('/api/racing/results', {
    date,
    categories: Array.isArray(categories) ? categories.join(',') : categories,
  });
  return { results: Array.isArray(body?.results) ? body.results : [], status };
}

/**
 * One race's price history: `{ history, status }`. FIVE CREDITS a call.
 *
 * The closing line, and the only way this app ever gets one for a race — the
 * card is a snapshot and the results carry the settling book's price, not every
 * book's. `stores/settlement.js` is the only caller and it buys at most four a
 * day, for races a bet was actually placed on. Nothing else may call it.
 */
export async function fetchRacingPriceHistory(raceId, { bookmakers = null } = {}) {
  const { body, status } = await ask('/api/racing/price-history', {
    race_id: raceId,
    // The series itself is never read — only each book's open and close — so the
    // points are left upstream rather than carried down to a phone.
    include_points: 'false',
    bookmakers: Array.isArray(bookmakers) ? bookmakers.join(',') : bookmakers,
  });
  return { history: body?.history ?? null, status };
}

/**
 * One race's Betfair starting prices: `{ bsp, status }`. FREE.
 *
 * The BSP is the close — the one price everybody could have had at the one
 * instant everybody had it — and the daily racing ingest already brings it for
 * every AU/NZ runner. So a racing bet's closing-line value costs nothing at all
 * for any race in an ingested month, and the five-credit price-history call is
 * only for the races the ingest has not reached yet (it publishes a month at a
 * time, so that is most of this week and none of last month).
 *
 * `bsp` is null when the month is not in the store. That is not an error and
 * the caller treats it as "ask the other way".
 */
export async function fetchRaceBsp(date, matchKey) {
  const { body, status } = await ask('/api/racing/bsp', { date, race: matchKey });
  return { bsp: body?.bsp ?? null, status };
}

/**
 * The day's free second opinions: `{ features, status }`. FREE.
 *
 * The Betfair Hub's own daily ratings, fetched once a day by the features job
 * and stored (`server/cron/features.mjs`); this hands back what it wrote. An
 * empty `sports` is the ordinary answer for a day the Hub has not published or
 * the job has not run, and it means the race page prints no rating line.
 */
export async function fetchRacingFeatures(date) {
  const { body, status } = await ask('/api/racing/features', { date });
  return { features: body?.sports ? body : null, status };
}

/** Firming and drifting runners: `{ movers, status }`. R6 reads these. */
export async function fetchRacingMovers({ categories = null } = {}) {
  const { body, status } = await ask('/api/racing/movers', {
    categories: Array.isArray(categories) ? categories.join(',') : categories,
  });
  return { movers: Array.isArray(body?.movers) ? body.movers : [], status };
}
